import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'

interface CreateExtensionViewProps {
  connection: DatabaseConnection
  database: string
  onExtensionInstalled: () => void
}

export function CreateExtensionView({ connection, database, onExtensionInstalled }: CreateExtensionViewProps) {
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    cascade: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showToast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      showToast('error', 'Extension name is required')
      return
    }

    try {
      setIsSubmitting(true)
      
      await AgentProxyService.createExtension(
        connection.id,
        database,
        formData.name.trim(),
        {
          version: formData.version.trim() || undefined
        }
      )
      
      showToast('success', `Extension "${formData.name}" installed successfully`)
      onExtensionInstalled()
    } catch (error) {
      console.error('Failed to install extension:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to install extension'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const commonExtensions = [
    'uuid-ossp',
    'pg_trgm',
    'hstore',
    'postgis',
    'pg_stat_statements',
    'pgcrypto',
    'ltree',
    'unaccent',
    'pg_buffercache',
    'pg_prewarm',
    'postgres_fdw',
    'dblink',
    'cube',
    'earthdistance',
    'fuzzystrmatch',
    'pgrowlocks',
    'pg_visibility',
    'bloom',
    'citext',
    'intarray',
    'tablefunc',
    'xml2',
    'btree_gin',
    'btree_gist',
    'dict_int',
    'dict_xsyn',
    'lo',
    'pageinspect',
    'pg_freespacemap',
    'pgstattuple',
    'sslinfo',
    'tcn',
    'tsm_system_rows',
    'tsm_system_time'
  ]

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="max-w-4xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Install Extension</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Install a PostgreSQL extension in database "{database}"
          </p>
        </div>

        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-6">
          {/* Extension Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Extension Name *
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => { handleInputChange('name', e.target.value) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter extension name (e.g., uuid-ossp)"
                required
              />
              
              {/* Common Extensions */}
              <div className="mt-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Common extensions:</p>
                <div className="flex flex-wrap gap-1">
                  {commonExtensions.map((ext) => (
                    <button
                      key={ext}
                      type="button"
                      onClick={() => { handleInputChange('name', ext) }}
                      className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                    >
                      {ext}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>


          {/* Version */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Version
            </label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => { handleInputChange('version', e.target.value) }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Specific version (optional)"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave empty to install the latest available version
            </p>
          </div>

          {/* Cascade */}
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.cascade}
                onChange={(e) => { handleInputChange('cascade', e.target.checked) }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Install dependent extensions (CASCADE)
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
              Automatically install any dependencies required by this extension
            </p>
          </div>

          {/* Extension Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
              Extension Installation Notes
            </h4>
            <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Extensions are installed at the database level</li>
              <li>• Some extensions require superuser privileges</li>
              <li>• Installation may fail if the extension is not available on the server</li>
              <li>• Use CASCADE option to automatically install dependencies</li>
              <li>• Check server logs if installation fails</li>
            </ul>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Installing...' : 'Install Extension'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
